import { getAccount } from "@solana/spl-token";
import {
  CrunchServiceWithContext,
  CrunchAccountServiceWithContext,
  getCoordinatorConfig,
  formatMicroUsdcToUsdc,
} from "@crunchdao/sdk";
import { EnhancedBaseCommand } from "@crunchdao/shared-cli";

export class DrainCommand extends EnhancedBaseCommand {
  private getCrunchService() {
    return CrunchServiceWithContext({
      program: this.program,
    });
  }

  private getCrunchAccountService() {
    return CrunchAccountServiceWithContext({
      program: this.program,
    });
  }

  async execute(crunchName: string, network?: string, showSolscan?: boolean): Promise<void> {
    try {
      const crunchService = this.getCrunchService();
      const crunchAccountService = this.getCrunchAccountService();

      const crunchAddress = crunchAccountService.getCrunchAddress(crunchName);
      const crunch = await crunchAccountService.getCrunch(crunchAddress);

      const tokenAccount = await getAccount(this.provider.connection, crunch.rewardVault);
      const drainAmount = formatMicroUsdcToUsdc(Number(tokenAccount.amount));

      await this.executeOrProposeInstructions(async () => {
        return [await crunchService.drainUsdcFromCrunchInstruction(crunchName, this.getSigner())];
      }, `Drain ${drainAmount} from crunch "${crunchName}"`);

      if (!this.isMultisigMode()) {
        const successData: any = {
          crunchName,
          rewardVaultAddress: crunch.rewardVault.toBase58(),
          drainedAmount: tokenAccount.amount.toString(),
        };

        if (showSolscan) {
          const solScanAddressLink = (address: string) =>
            `https://solscan.io/address/${address}?cluster=${network}`;

          successData.solscanLinks = {
            tokenAccount: solScanAddressLink(crunch.rewardVault.toBase58()),
          };
        }

        this.success("USDC drained from crunch successfully", successData);
      }
    } catch (error) {
      await this.handleDrainError(error, crunchName);
    }
  }

  private async handleDrainError(error: any, crunchName: string): Promise<never> {
    // Check for checkpoint too young error
    const isCheckpointTooYoung = error?.error?.errorCode?.code === "CheckpointTooYoung";

    if (isCheckpointTooYoung) {
      return await this.handleCheckpointTooYoungError(error, crunchName);
    }

    // Check for crunch margin not paid out error
    const isMarginNotPaidOut = error?.error?.errorCode?.code === "CrunchMarginNotPaidout";

    if (isMarginNotPaidOut) {
      const customError = this.createEnhancedError(
        `Cannot drain USDC from crunch "${crunchName}". The crunch margins must be paid out before draining remaining USDC.`,
        "CrunchMarginNotPaidOutError",
        "CRUNCH_MARGIN_NOT_PAID_OUT",
        {
          crunchName,
          errorCode: error.error?.errorCode?.code || "CrunchMarginNotPaidout",
          errorNumber: error.error?.errorCode?.number || 6010,
        },
        error
      );

      return this.handleError(customError);
    }

    // Fall back to generic transaction error handling
    return this.handleTransactionError(error, crunchName);
  }

  private async handleCheckpointTooYoungError(error: any, crunchName: string): Promise<never> {
    try {
      const crunchAccountService = this.getCrunchAccountService();

      // Get coordinator config for claimExpirySeconds
      const coordinatorConfig = await getCoordinatorConfig(this.program);
      const claimExpirySeconds = Number(coordinatorConfig.claimExpirySeconds);

      // Get current checkpoint for timestamp
      const crunchAddress = crunchAccountService.getCrunchAddress(crunchName);
      const currentCheckpoint = await crunchAccountService.getCurrentCheckpoint(crunchAddress);

      if (currentCheckpoint) {
        // Convert checkpoint timestamp (BN) to human readable date
        const checkpointTimestamp = Number(currentCheckpoint.account.timestamp);
        const expiryTimestamp = checkpointTimestamp + claimExpirySeconds;
        const expiryDate = new Date(expiryTimestamp * 1000); // Convert to milliseconds
        const currentDate = new Date();

        const timeUntilExpiry = expiryDate.getTime() - currentDate.getTime();
        const hoursUntilExpiry = Math.ceil(timeUntilExpiry / (1000 * 60 * 60));

        let waitMessage = "";
        if (timeUntilExpiry > 0) {
          if (hoursUntilExpiry > 24) {
            const daysUntilExpiry = Math.ceil(hoursUntilExpiry / 24);
            waitMessage = `Wait ${daysUntilExpiry} day${daysUntilExpiry > 1 ? "s" : ""}`;
          } else {
            waitMessage = `Wait ${hoursUntilExpiry} hour${hoursUntilExpiry > 1 ? "s" : ""}`;
          }
        } else {
          waitMessage = "Should be available now (may need a few minutes)";
        }

        const customError = this.createEnhancedError(
          `Cannot drain USDC from crunch "${crunchName}" yet. The checkpoint is too young and must wait for the claim expiry period. ${waitMessage} until ${expiryDate.toLocaleString()}.`,
          "CheckpointTooYoungError",
          "CHECKPOINT_TOO_YOUNG",
          {
            crunchName,
            checkpointTimestamp,
            claimExpirySeconds,
            expiryDate: expiryDate.toISOString(),
            currentDate: currentDate.toISOString(),
            hoursUntilExpiry: Math.max(0, hoursUntilExpiry),
            errorCode: error.error?.errorCode?.code || "CheckpointTooYoung",
            errorNumber: error.error?.errorCode?.number || 6025,
          },
          error
        );

        return this.handleError(customError);
      } else {
        // Fallback if we can't get checkpoint info
        const customError = this.createEnhancedError(
          `Cannot drain USDC from crunch "${crunchName}" yet. The checkpoint is too young and must wait for the claim expiry period.`,
          "CheckpointTooYoungError",
          "CHECKPOINT_TOO_YOUNG",
          {
            crunchName,
            claimExpirySeconds,
            errorCode: error.error?.errorCode?.code || "CheckpointTooYoung",
            errorNumber: error.error?.errorCode?.number || 6025,
          },
          error
        );

        return this.handleError(customError);
      }
    } catch (fetchError) {
      // If we can't fetch the coordinator or checkpoint info, fall back to a simpler error
      let fetchErrorMessage: string;
      if (typeof fetchError === "object" && fetchError !== null && "message" in fetchError) {
        fetchErrorMessage = String((fetchError as { message?: unknown }).message);
      } else {
        fetchErrorMessage = String(fetchError);
      }
      const customError = this.createEnhancedError(
        `Cannot drain USDC from crunch "${crunchName}" yet. The checkpoint is too young and must wait for the claim expiry period.`,
        "CheckpointTooYoungError",
        "CHECKPOINT_TOO_YOUNG",
        {
          crunchName,
          errorCode: error.error?.errorCode?.code || "CheckpointTooYoung",
          errorNumber: error.error?.errorCode?.number || 6025,
          fetchError: fetchErrorMessage,
        },
        error
      );

      return this.handleError(customError);
    }
  }
}
